import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { compose } from 'ramda';
import { withHuggg, withLocations, withRedirect } from 'hoc';
import { useAnalytics } from 'hooks';
import { Layout } from 'layouts';
import { LocationsDialog, Huggg } from 'components';
import { Divider, Markdown, useThemeContext } from '@huggg/react-components';
import {
  ExpirationDate as UIExpirationDate,
  PoweredByHuggg as UIPoweredByHuggg,
  BackButton,
} from 'components/ui';
import { getBasePathname } from 'utilities/huggg';
import { StashButton, RedeemButton } from './Actions';
import styles from './more-details.module.scss';
import { getIsGenerating, clearError } from 'redux/modules/voucher';
import { getBrandName } from 'utilities/huggg';
import ChooseButton from './Actions/ChooseButton';
import {
  getSegments,
  getVoucherExpiry,
  hasVoucherToClaim,
  hasFulfilledVoucher,
  isVoucherV2,
} from 'redux/modules/huggg';
import classnames from 'classnames';

export const ScreensMoreDetails = (props) => {
  const {
    huggg,
    segments,
    locations,
    isStashed,
    clearError,
    voucherExpiry,
    isVirginBrandProduct,
    isVoucherV2,
    hasFulfilledVoucher,
  } = props;

  const backAction = useCallback(() => getBasePathname(huggg), [huggg]);
  const isMultiSegment = useMemo(() => segments.length > 1, [segments]);

  const analytics = useAnalytics();
  const hasLocations = Boolean(locations && locations.length);

  const { shouldShowPoweredByHuggg } = useThemeContext();

  useEffect(() => {
    huggg.isSuperhuggg
      ? analytics.screenViewedSuperhugggMoreInfo()
      : analytics.screenViewedHugggMoreInfo();
  }, [analytics, huggg]);

  const getCtaButton = useCallback(() => {
    const isOOS = Boolean(huggg?.purchase?.out_of_stock);
    if (huggg.isSuperhuggg) {
      return (
        <StashButton huggg={huggg} isStashed={isStashed} disabled={isOOS} />
      );
    } else if (isMultiSegment) {
      return <ChooseButton huggg={huggg} disabled={isOOS} />;
    }
    return <RedeemButton huggg={huggg} onClick={clearError} disabled={isOOS} />;
  }, [huggg, isStashed, clearError, isMultiSegment]);

  return (
    <Layout backAction={backAction}>
      <div className="container">
        <Huggg huggg={huggg} data-test-id="landing-screen-huggg" />

        {shouldShowPoweredByHuggg && <UIPoweredByHuggg />}

        <Divider />

        <div
          className={classnames(styles.description, {
            [styles.virginDescription]: isVirginBrandProduct,
          })}
        >
          <Markdown value={huggg?.purchase?.recipient_description} centerText />
        </div>

        <Divider />
      </div>

      {hasLocations && (
        <LocationsDialog
          brandName={getBrandName(huggg)}
          locations={locations}
        />
      )}

      <div className={styles.buttonContainer}>
        <UIExpirationDate
          huggg={huggg}
          voucherExpiry={voucherExpiry}
          isVoucherV2={isVoucherV2}
          hasFulfilledVoucher={hasFulfilledVoucher}
        />
        {getCtaButton()}
        <BackButton className={styles.backButton} />
      </div>
    </Layout>
  );
};

/**
 * This is here because if we SkipToClaim, we might redeem on this page.
 * In that situation we need to subsequently skip to the redeem page
 * to display the voucher and success message(s) / details.
 *
 * We _don't_ want to do this on page-reload and revisit though.
 */
const recentlyRedeemed = (props) => [
  (props.isFirstRedeemed || props.voucherGenerating) &&
    props?.huggg?.vouchers?.length > 0 &&
    !props.hasVoucherToClaim,
  `/${props.huggg.shortcode}/redeem`,
];

ScreensMoreDetails.propTypes = {
  huggg: PropTypes.object.isRequired,
  locations: PropTypes.array.isRequired,
  isStashed: PropTypes.bool.isRequired,
  isFirstRedeemed: PropTypes.bool.isRequired,
  voucherGenerating: PropTypes.bool.isRequired,
  voucherExpiry: PropTypes.string,
  clearError: PropTypes.func.isRequired,
  segments: PropTypes.array.isRequired,
  hasVoucherToClaim: PropTypes.bool.isRequired,
  isVirginBrandProduct: PropTypes.bool,
  isVoucherV2: PropTypes.bool,
  hasFulfilledVoucher: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  voucherGenerating: getIsGenerating(state),
  voucherExpiry: getVoucherExpiry(state),
  segments: getSegments(state),
  hasVoucherToClaim: hasVoucherToClaim(state),
  isVoucherV2: isVoucherV2(state),
  hasFulfilledVoucher: hasFulfilledVoucher(state),
});

export default compose(
  withHuggg,
  withLocations,
  connect(mapStateToProps, {
    clearError,
  }),
  withRedirect(recentlyRedeemed),
)(ScreensMoreDetails);
